import React, { useState } from 'react';
import { Button, CircularProgress, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { OrgGraphVisRef } from 'org-graph/OrgGraphVis';
import { useCases } from '../../contexts/CasesContext';
import { useCompressedFetch } from '../../hooks/compressedFetch';
import { useToast } from '../../components/Toast';
import getUser from '../../getUser';
import { useTranslation } from 'react-i18next';

interface CaseCreateButtonProps {
  disabled: boolean;
  orgNumber?: string;
  orgGraphRef?: React.RefObject<OrgGraphVisRef>;
  currentCaseId?: string | null;
  currentCaseName?: string | null;
}

export default function CaseCreateButton({ 
  disabled,
  orgNumber,
  orgGraphRef,
  currentCaseId,
  currentCaseName
}: CaseCreateButtonProps) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { triggerRefresh } = useCases();
  const toast = useToast();
  // const theme = useTheme();
  //const isDarkMode = theme.palette.mode === 'dark';

  const compressedFetch = useCompressedFetch();
  
  const islandStyles = {
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    border: 1,
    borderColor: 'divider',
    padding: '8px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    bgcolor: 'background.paper',
    color: 'text.primary',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
    },
    transition: 'box-shadow 0.2s ease-in-out',
    cursor: 'pointer'
  };
  
  const handleCreateCase = async () => {
    try {
      setIsLoading(true);
      
      let defaultCaseName: string;
      
      // Create default case name with ISO date format (yyyy-mm-dd)
      defaultCaseName = t('assessment.details.defaultCaseName', { company: orgNumber || t('monitoring.overview.company'), date: new Date().toISOString().split('T')[0] });
      
      // For updates, use the existing case name without prompting
      // For new cases, prompt the user for a name
      let finalCaseName: string;
      
      if (currentCaseId) {
        // Updating existing case - use a default name since we don't prompt for updates
        finalCaseName = currentCaseName || defaultCaseName;
      } else {
        // Creating new case - prompt user for case name
        const caseName = window.prompt(
          t('assessment.details.caseNamePrompt'),
          defaultCaseName
        );
        
        // If user cancelled the prompt, exit
        if (caseName === null) {
          setIsLoading(false);
          return;
        }
        
        // If user provided empty name, use default
        finalCaseName = caseName.trim() || defaultCaseName;
      }

      // Call getGraphState from OrgGraphVis if available
      const graphState = orgGraphRef?.current?.getGraphState();

      if (!graphState) {
        console.error('Could not get graph state');
        setIsLoading(false);
        return;
      }

      const casePayload = {
        name: finalCaseName,
        orgNumber: graphState.orgNumber,
        graphData: {
          nodes: graphState.nodes,
          relationships: graphState.relationships
        },
        hiddenStateStack: graphState.hiddenStateStack,
        selectedGraphTypes: graphState.selectedGraphTypes,
        showBadPathsOnly: graphState.showBadPathsOnly,
        contextId: graphState.contextId
      };

      const apiUrl = currentCaseId 
        ? `${process.env.REACT_APP_API_ENDPOINT}/v1/cases/${currentCaseId}`
        : `${process.env.REACT_APP_API_ENDPOINT}/v1/cases`;
      
      const method = currentCaseId ? 'PUT' : 'POST';

      const response = await compressedFetch(apiUrl, {
        method: method,
        headers: {
          Authorization: `Bearer ${getUser()?.access_token}`,
          "Content-Type": "application/octet-stream",
        },
        body: JSON.stringify(casePayload)
      });

      if (response.ok) {
        toast.success(t(currentCaseId ? 'assessment.details.caseUpdated' : 'assessment.details.caseSaved'));
        
        // Trigger refresh of cases sidebar
        triggerRefresh();
        
        // For new case creation, get the case ID from response text for navigation
        if (!currentCaseId) {
          const caseId = await response.text();
          if (caseId && caseId.trim()) {
            navigate(`/companyassessment/${orgNumber}?caseId=${caseId.trim()}`);
          }
        }
      } else {
        const errorText = await response.text();
        console.error(`Failed to ${currentCaseId ? 'update' : 'create'} case:`, response.statusText, errorText);
        toast.error(t('assessment.details.caseSaveFailed', { message: `${response.status} ${response.statusText}` }));
      }
    } catch (error) {
      console.error(`Error ${currentCaseId ? 'updating' : 'creating'} case:`, error);
      toast.error(t('assessment.details.caseSaveFailed', { message: error instanceof Error ? error.message : t('common.states.unknown') }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Box sx={islandStyles} onClick={!disabled && !isLoading ? handleCreateCase : undefined}>
        <Button
          disabled={disabled || isLoading}
          variant="text"
          size="small"
          startIcon={isLoading ? <CircularProgress size={20} /> : undefined}
          sx={{
            color: 'primary.main',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            fontWeight: 600,
            fontSize: 'var(--font-size-xs)',
            minWidth: 'auto',
            padding: '4px 8px',
            pointerEvents: 'none' // Let the Box handle the click to behave like the island
          }}
        >
          {isLoading ? t('monitoring.overview.saving') : t(currentCaseId ? 'assessment.cases.update' : 'assessment.details.saveNewCase')}
        </Button>
      </Box>
    </>
  );
}
